import Promise from 'bluebird';
import $ from 'jquery';
import _ from 'underscore';

export type AjaxXhr = ReturnType<typeof $.ajax> | null;

export type AjaxTextStatus = string;

export type AjaxErrorThrown = string | undefined;

export type AjaxSuccessCallback<TData = unknown> = (
  data: TData,
  status: AjaxTextStatus,
  inputData: unknown,
  onSuccessEventData: unknown,
  xhr: AjaxXhr
) => void;

export type AjaxErrorCallback = (
  xhr: AjaxXhr,
  textStatus: AjaxTextStatus,
  errorThrown: AjaxErrorThrown
) => void;

export type AjaxRedirectHandler = (
  redirectUrl: string,
  message: unknown,
  data: unknown
) => void;

export interface AjaxRequestOptions<TData = unknown> {
  onSuccess?: AjaxSuccessCallback<TData>;
  onAjaxError?: AjaxErrorCallback;
  redirectHandler?: AjaxRedirectHandler;
  [key: string]: unknown;
}

export type AjaxRequestFunction = <TData = unknown>(options: AjaxRequestOptions<TData>) => void;

export class AjaxError extends Error {
  errorThrown: AjaxErrorThrown;
  textStatus: AjaxTextStatus;
  xhr: AjaxXhr;
  constructor(xhr: AjaxXhr, textStatus: AjaxTextStatus, errorThrown: AjaxErrorThrown) {
    super(errorThrown);
    this.name = 'AjaxError';
    this.xhr = xhr;
    this.textStatus = textStatus;
    this.errorThrown = errorThrown;
  }
}

export class JsonError extends Error {
  data: unknown;
  status: AjaxTextStatus;
  xhr: AjaxXhr;
  constructor(data: unknown, status: AjaxTextStatus, xhr: AjaxXhr) {
    super();
    this.name = 'JsonError';
    this.data = data;
    this.status = status;
    this.xhr = xhr;
  }
}

function generateSuccessAndFail<TData>(
  options: AjaxRequestOptions<TData>,
  resolve: (value: TData) => void,
  reject: (reason?: unknown) => void
) {
  const prevOnSuccess = $.isFunction(options.onSuccess) ? options.onSuccess : _.noop;
  const prevOnAjaxError = $.isFunction(options.onAjaxError) ? options.onAjaxError : _.noop;

  return _.extend(options, {
    onSuccess: (data: TData, status: AjaxTextStatus, inputData: unknown, onSuccessEventData: unknown, xhr: AjaxXhr) => {
      prevOnSuccess(data, status, inputData, onSuccessEventData, xhr);
      $.advertiser.ajaxSuccess(
        data,
        status,
        xhr,
        () => resolve(data),
        () => reject(new JsonError(data, status, xhr)),
        options.redirectHandler
      );
    },
    onAjaxError: (xhr: AjaxXhr, textStatus: AjaxTextStatus, errorThrown: AjaxErrorThrown) => {
      prevOnAjaxError(xhr, textStatus, errorThrown);
      reject(new AjaxError(xhr, textStatus, errorThrown));
    },
  });
}

function createPromise<TData>(requestFunction: AjaxRequestFunction, options: AjaxRequestOptions<TData>) {
  return new Promise<TData>((resolve, reject) => {
    requestFunction(generateSuccessAndFail(options, resolve, reject));
  });
}

export function post<TData = unknown>(options: AjaxRequestOptions<TData>) {
  return createPromise($.advertiser.ajaxPost, options);
}

export function get<TData = unknown>(options: AjaxRequestOptions<TData>) {
  return createPromise($.advertiser.ajaxGet, options);
}

export function patch<TData = unknown>(options: AjaxRequestOptions<TData>) {
  return createPromise($.advertiser.ajaxPatch, options);
}
